import { ResizeObserver as Polyfill } from "@juggle/resize-observer";
import { useMemo, useRef } from "react";
import type {
    Options as MeasureOptions,
    RectReadOnly,
} from "react-use-measure";
import useMeasure from "react-use-measure";
import { useUpdateEffect } from "../use-update-effect";

type MeasureRef =
    | ((element: HTMLElement | SVGElement | null) => void)
    | ((element: HTMLOrSVGElement | null) => void);

const useSizeTracker = (
    onSizeChange?: (size: RectReadOnly) => void,
    options: MeasureOptions = {},
): readonly [MeasureRef, RectReadOnly] => {
    const [measureRef, size] = useMeasure({
        debounce: 100,
        polyfill: Polyfill,
        ...options,
    });

    const onSizeChangeRef = useRef(onSizeChange);
    onSizeChangeRef.current = onSizeChange;

    useUpdateEffect(
        function onSizeChange() {
            if (!onSizeChangeRef.current) return;
            onSizeChangeRef.current(size);
        },
        [size],
    );

    return useMemo(() => [measureRef, size] as const, [measureRef, size]);
};

export { useSizeTracker };
export type { MeasureRef, RectReadOnly };
